import { useMemo } from 'react';
import { TEST_RUNS } from '@elementary/common-ui/constants/queryKeys';
import useGraphqlPagedQuery from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import { DEFAULT_DAYS_BACK, PAGE_SIZE } from '@elementary/common-ui/constants';
import { type ApiTestRunData } from '@elementary/common-ui/pages';
import { getTestRunsTableDataQuery } from '../api';
import { ApiTestRunsTableDataPagedResponse } from '../types';
import { mapTestRunRows } from '../utils';
import useTestRunsQueryContext from './useTestRunsQueryContext';

const useTestRuns = ({
  enabled = true,
  onError,
  search,
}: {
  enabled?: boolean;
  onError?: () => void;
  search?: string;
}) => {
  const {
    envid,
    selectedAssetIds,
    selectedTestIds,
    selectedIdsHash,
    hasSelectedNodes,
    urlFiltersDebounced,
    urlSort,
    testExecutionFilters,
    sort,
    filterOptions,
    isFilterOptionsFetching,
  } = useTestRunsQueryContext();

  const { data, isLoading, isFetching, isError, fetchNextPage, hasNextPage } =
    useGraphqlPagedQuery<ApiTestRunData, ApiTestRunsTableDataPagedResponse>({
      queryKey: [
        TEST_RUNS,
        envid,
        selectedIdsHash,
        DEFAULT_DAYS_BACK,
        urlFiltersDebounced,
        urlSort,
        search,
      ],
      query: getTestRunsTableDataQuery,
      variables: {
        testIds: selectedTestIds.length ? selectedTestIds : null,
        assetIds: selectedAssetIds.length ? selectedAssetIds : null,
        daysBack: DEFAULT_DAYS_BACK,
        testExecutionFilters,
        sort,
        search: search == '' ? null : search,
        limit: PAGE_SIZE,
      },
      select: (data) => data.latestTestExecutionsPaged,
      onError: onError,
      enabled: enabled && hasSelectedNodes,
      envId: envid,
    });

  const tableData = useMemo(() => mapTestRunRows(data), [data]);

  return {
    tableData,
    filterOptions,
    isFetching: isLoading || isFetching,
    isFilterOptionsFetching,
    isError,
    fetchNextPage,
    hasNextPage,
  };
};

export default useTestRuns;
